import {
  type Filter,
  type FilterFieldOptions,
  type FilterValue,
  type FilterOption,
} from '../../types/filters.types';
import { SearcherDocumentSearchFilterOperator } from 'src/api/generated';
import { getDefaultValueForFieldType } from './values.util';
import { isArrayOperator, isNoValueOperator } from './operators.util';

/**
 * Validates and normalizes a filter value based on the operator type
 * @returns The normalized value if valid, null if invalid
 */
const validateAndNormalizeValue = (
  value: FilterValue,
  operator: SearcherDocumentSearchFilterOperator,
  fieldOption?: FilterFieldOptions,
  getOptionsForField?: (fieldOption?: FilterFieldOptions) => FilterOption[]
): FilterValue | null => {
  if (isNoValueOperator(operator)) {
    return undefined;
  }

  const isEmptyValue =
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);
  if (isEmptyValue) {
    if (!fieldOption || !getOptionsForField) {
      return null;
    }

    return getDefaultValueForFieldType(
      fieldOption.type,
      getOptionsForField(fieldOption)
    );
  }

  const isArrayOp = isArrayOperator(operator);
  const isValueArray = Array.isArray(value);

  if (isArrayOp && !isValueArray) {
    return [String(value)];
  }
  if (!isArrayOp && isValueArray) {
    return value.length > 0 ? value[0] : null;
  }

  return value;
};

/**
 * Converts a date-only value to datetime-local format with time at 00:00
 */
const convertDateToDateTime = (value: FilterValue): string => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00`;
  }
  return String(value);
};

/**
 * Gets appropriate default value for a date field based on operator
 */
const getDefaultDateValue = (
  operator: SearcherDocumentSearchFilterOperator,
  includeTime = false
): string => {
  const today = new Date().toISOString().split('T')[0];

  // EQ operator uses date input (YYYY-MM-DD)
  if (operator === SearcherDocumentSearchFilterOperator.EQ && !includeTime) {
    return today;
  }

  // GT, LT, GTE, LTE use datetime-local input (YYYY-MM-DDTHH:MM)
  return `${today}T00:00`;
};

/**
 * Checks if operator requires datetime-local format
 */
const requiresDateTimeFormat = (
  operator: SearcherDocumentSearchFilterOperator
): boolean => {
  return [
    SearcherDocumentSearchFilterOperator.GT,
    SearcherDocumentSearchFilterOperator.LT,
    SearcherDocumentSearchFilterOperator.GTE,
    SearcherDocumentSearchFilterOperator.LTE,
  ].includes(operator);
};

/**
 * Updates a filter's operator and normalizes its value accordingly
 */
export const updateFilterOperator = (
  filter: Filter,
  operator: SearcherDocumentSearchFilterOperator,
  getOptionsForField: (fieldOption?: FilterFieldOptions) => FilterOption[],
  fieldOption?: FilterFieldOptions
): Filter => {
  if (!fieldOption) return filter;

  if (isNoValueOperator(operator)) {
    return {
      ...filter,
      operator,
      value: undefined,
    };
  }

  const normalizedValue = validateAndNormalizeValue(
    filter.value,
    operator,
    fieldOption,
    getOptionsForField
  );

  let finalValue: FilterValue;

  // Special handling for date fields
  if (fieldOption.type === 'date') {
    if (normalizedValue) {
      // Convert existing date-only value to datetime format if needed
      if (requiresDateTimeFormat(operator) || fieldOption.includeTime) {
        finalValue = convertDateToDateTime(normalizedValue);
      } else {
        finalValue = normalizedValue;
      }
    } else {
      // Get appropriate default value based on operator
      finalValue = getDefaultDateValue(operator, fieldOption.includeTime);
    }
  } else {
    // Non-date fields: use normalized value or default
    const defaultValue = getDefaultValueForFieldType(
      fieldOption.type,
      getOptionsForField(fieldOption)
    );
    finalValue = normalizedValue ?? defaultValue;
  }

  return {
    ...filter,
    operator,
    value: finalValue,
  };
};

/**
 * Updates a filter's value and normalizes it according to the operator
 */
export const updateFilterValue = (
  filter: Filter,
  value: FilterValue
): Filter | null => {
  const normalizedValue = validateAndNormalizeValue(value, filter.operator);
  if (normalizedValue === null) {
    return filter;
  }

  return {
    ...filter,
    value: normalizedValue,
  };
};
